import { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, IconButton, Button } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { doc, setDoc, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../../config/firebase'; // Import your Firestore instance
// Import custom icons
import { ReactComponent as LikeDisabledIcon } from '../../assets/images/like-disabled-icon.svg';
import { ReactComponent as LikeEnabledIcon } from '../../assets/images/like-enabled-icon.svg';
import { ReactComponent as DislikeDisabledIcon } from '../../assets/images/dislike-disabled-icon.svg';
import { ReactComponent as DislikeEnabledIcon } from '../../assets/images/dislike-enabled-icon.svg';
import { ReactComponent as TermsIcon } from '../../assets/images/terms-icon.svg';
import { ReactComponent as CouponIcon } from '../../assets/images/coupon-icon.svg';
import { ReactComponent as CopyIcon } from '../../assets/images/copy-icon.svg';
import useCouponInteraction from '../../hooks/useCouponInteraction';

const DesktopCoupon = ({ coupon, language }) => {
  const { isLiked, isDisliked, canInteract, handleLike, handleDislike, handleTrackClick, handleCopyCode } =
    useCouponInteraction(coupon);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { t } = useTranslation();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(coupon.promo_code); // Copy promo code to clipboard
    handleCopyCode(); // Track the copy in Firestore
    setTooltipOpen(true); // Show the tooltip
    setTimeout(() => {
      setTooltipOpen(false); // Hide the tooltip after 2 seconds
    }, 2000);
  };

  const handleCouponClick = () => {
    handleTrackClick(); // Track the click in Firestore
    window.open(coupon.store_url, '_blank'); // Open the coupon URL in a new tab
  };

  const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.dark,
      boxShadow: theme.shadows[1],
      fontSize: 15
    },
  }));

  const storeName = language === 'AR' ? coupon.store_name_arabic : coupon.store_name_english;
  const terms = language === 'AR' ? coupon.offer_terms_arabic : coupon.offer_terms_english;

  const getLikeIcon = () => {
    return isLiked ? <LikeEnabledIcon /> : <LikeDisabledIcon />;
  };

  const getDislikeIcon = () => {
    return isDisliked ? <DislikeEnabledIcon /> : <DislikeDisabledIcon />;
  };

  return (
    <Card 
      elevation={0} // Removes shadow
      sx={{ 
        height: 340,
        position: 'relative',
        borderRadius: 5,
        // '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        position: 'absolute',
        top: 8,
        left: 8,
        right: 8
      }}>
        <IconButton 
          onClick={handleDislike}
          disabled={!canInteract}
          sx={{ 
            color: isDisliked ? 'error.main' : 'action.disabled',
            '&:hover': { 
              color: 'error.main',
              transform: 'scale(1.1)',
              transition: 'all 0.2s'
            }
          }}
          size="small"
        >
          {getDislikeIcon()}
        </IconButton>
        <IconButton 
          onClick={handleLike}
          disabled={!canInteract}
          sx={{ 
            color: isLiked ? 'primary.main' : 'action.disabled',
            '&:hover': { 
              color: 'primary.main',
              transform: 'scale(1.1)',
              transition: 'all 0.2s'
            }
          }}
          size="small"
        >
          {getLikeIcon()}
        </IconButton>
      </Box>
      
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        mt: 2,
        px: 2,
        height: '100%' // Makes the CardContent take full height
      }}>
        <Box
          sx={{
            width: 85,
            height: 85,
            minHeight: 85,
            position: 'relative',
            mb: 2,
            top: -12,
            borderRadius: '50%',
            border: '1px solid',
            borderColor: 'grey.300',
            bgcolor: 'white',
            overflow: 'hidden',
            cursor: 'pointer', // Makes it clickable
          }}
          onClick={handleCouponClick} // Track and open the coupon
        >
          {/* The actual logo */}
          <Box
            component="img"
            src={coupon.logo}
            alt={storeName}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
          />

          {/* Black overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0, 0, 0, 0.6)', // Black overlay with transparency
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0, // Initially hidden
              transition: 'opacity 0.3s ease', // Smooth transition
              '&:hover': {
                opacity: 1, // Show overlay on hover
              },
            }}
          >
            {/* Open link icon */}
            <LaunchIcon
              sx={{
                color: 'white',
                fontSize: 24,
              }}
            />
          </Box>
        </Box>
        
        <Typography variant="h6" sx={{ mb: 0.5, textAlign: 'center' }}>
          {storeName}
        </Typography>
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 1, // Consistent bottom margin
          }}
        >
          <TermsIcon
            style={{
              width: 18,
              height: 18,
              flexShrink: 0, // Prevents icon from shrinking
            }}
          />
          <Tooltip title={terms} arrow>
            <Typography
              variant="body2"
              sx={{
                textAlign: 'start', // Aligns text to the start
                display: 'inline-block', // Ensures the text is treated as a single line
                whiteSpace: 'nowrap', // Restricts the text to one line
                overflow: 'hidden', // Hides overflow text
                textOverflow: 'ellipsis', // Adds ellipsis for overflow text
                maxWidth: 200, // Restricts the width of the text container
                fontWeight: 300,
                fontSize: 14,
                color: 'text.secondary',
                cursor: 'pointer', // Indicates the text is clickable
              }}
            >
              {terms}
            </Typography>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center', // Centers items vertically
            justifyContent: 'center', // Centers items horizontally
            mb: 3,
            gap: 1,
            borderRadius: 1,
            height: 48, // Fixed height to ensure consistent vertical centering
          }}
        >
          <CouponIcon 
            style={{ 
              width: 18,
              height: 18,
              flexShrink: 0
            }} 
          />
          <Typography 
            variant="h5" 
            sx={{ 
              fontFamily: 'monospace',
              fontSize: 18,
              fontWeight: 500,
              color: 'primary.main',
              bgcolor: 'primary.primary_50',
              px: 2,
              py: 1,
              borderRadius: 1,
              lineHeight: 1, // Ensures text is vertically centered
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {coupon.promo_code}
          </Typography>
        </Box>
        
        <LightTooltip
          title={t('actions.copied')} // Tooltip message
          open={tooltipOpen} // Control visibility
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          <Button
            variant="contained"
            startIcon={
              <CopyIcon 
                style={{ 
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  marginRight: 10
                }} 
              />
            }
            onClick={handleCopy}
            sx={{
              textTransform: 'none',
              fontSize: 16,
              px: 3,
              py: 1,
              fontWeight: 500,
              borderRadius: 5,
              mt: 'auto',
              mb: 3,
              boxShadow: 'none'
            }}
          >
            {t('actions.copy_code')}
          </Button>
        </LightTooltip>
      </CardContent>
    </Card>
  );
};

export default DesktopCoupon;